import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { BotCampaignRecipientInterface, BotCampaignSummary } from "@swivl-mono/types";
import { Button, ButtonSize, ButtonVariant, Panel, Spinner } from "@swivl-mono/ui";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { parseOptionalNumber } from "../../../Helpers/CRM/searchParams.Helper";
import { useUrlState } from "../../../Hooks/useUrlState";
import { Member } from "../../../Models/Member/Member.Current";
import { NavBar } from "../../../Models/NavBar/NavBar.Model";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { UsersModel } from "../../../Models/Users/Users.Model";
import UserVarsModel from "../../../Models/Variables/UserVars.Model";
import UserDetails, { UserDetailsTabID } from "../../ContactCenter/Conversations/DetailsColumn/UserDetails";
import { CRMNavContent } from "../Components/CRM.NavTabs";

/**
 * CRM → SMS Campaigns (ENG-3114): every campaign an agent sent, and — per recipient — exactly
 * what happened: sent (with the delivered text), skipped (with the reason), or failed. This is
 * the post-facto skip visibility the spec requires. Read-only.
 */

const PAGE = 25;

/** Entity dates arrive as Date objects in-process and ISO strings over the wire. */
function formatWhen(value: Date | string | undefined, format: string): string {
  if (!value) return "";
  const dt = typeof value === "string" ? DateTime.fromISO(value) : DateTime.fromJSDate(value);
  return dt.isValid ? dt.toFormat(format) : "";
}

const STATUS_CLASS: Record<string, string> = {
  sent: "bg-teal-100 text-teal-800",
  simulated: "bg-purple-100 text-purple-800",
  skipped: "bg-yellow-100 text-yellow-800",
  failed: "bg-red-100 text-red-800",
  queued: "bg-grey-100 text-grey-700"
};

export function SmsCampaignsView(): React.ReactElement {
  const [campaigns, setCampaigns] = useState<BotCampaignSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState<BotCampaignSummary | null>(null);
  const [recipients, setRecipients] = useState<BotCampaignRecipientInterface[] | null>(null);

  // A recipient's User id opens the same right-side customer flyout the CRM Users page has
  // (ENG-3812, Chuck): keyed by `userId` in the URL, loaded the way that page loads it.
  const { searchParams, setSearchParam } = useUrlState();
  const selectedUserId = parseOptionalNumber(searchParams.get("userId"));
  const { users } = UsersModel.useState();
  const { vars } = UserVarsModel.useState((s) => s);
  const selectedUser = selectedUserId !== undefined ? users?.[selectedUserId] : undefined;
  const userVars = useMemo(() => (selectedUserId && vars ? vars[selectedUserId] : undefined), [vars, selectedUserId]);

  useEffect(() => {
    NavBar.set(<CRMNavContent />, null, null, "SMS Campaigns");
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      UserVarsModel.loadForUser(selectedUserId);
      UsersModel.loadUsersByIds([selectedUserId]);
    }
  }, [selectedUserId]);

  const load = useCallback(async (after?: number): Promise<void> => {
    if (!Member.orgId) return;
    setLoading(true);
    try {
      const page = await API.SnowyCampaigns.list(Member.orgId, { after, limit: PAGE });
      setCampaigns((prev) => (after ? [...prev, ...page] : page));
      setHasMore(page.length === PAGE);
    } catch {
      AlertsModel.errorToast("Could not load campaigns");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Live outcomes: a recipient goes queued → sent/skipped/failed within seconds of a send, but
  // this view fetched once — the first real send sat on "queued" here long after it had already
  // failed (2026-09-03). While anything is still queued, poll quietly; stop when every outcome
  // is terminal. The Refresh button stays the loud, error-reporting path.
  const hasQueued = campaigns.some((c) => c.queuedCount > 0) || (recipients ?? []).some((r) => r.status === "queued");
  useEffect(() => {
    if (!hasQueued || !Member.orgId) return;
    const orgId = Member.orgId;
    const timer = setInterval(() => {
      void (async () => {
        try {
          const page = await API.SnowyCampaigns.list(orgId, { limit: PAGE });
          setCampaigns((prev) => (prev.length > page.length ? [...page, ...prev.slice(page.length)] : page));
          if (selected) setRecipients(await API.SnowyCampaigns.recipients(orgId, selected.id));
        } catch {
          // quiet poll — errors surface through the Refresh button's path
        }
      })();
    }, 5000);
    return () => clearInterval(timer);
  }, [hasQueued, selected]);

  const handleSelect = async (campaign: BotCampaignSummary): Promise<void> => {
    if (!Member.orgId) return;
    setSelected(campaign);
    setRecipients(null);
    try {
      setRecipients(await API.SnowyCampaigns.recipients(Member.orgId, campaign.id));
    } catch {
      AlertsModel.errorToast("Could not load recipients");
      setRecipients([]);
    }
  };

  const handleRefresh = (): void => {
    setSelected(null);
    setRecipients(null);
    void load();
  };

  return (
    <>
      <div className="flex h-full min-w-0 flex-1 flex-col overflow-y-auto bg-grey-50 p-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs text-grey-500">
            Every SMS campaign sent from the Contact Center, with each recipient&apos;s outcome. Campaigns are sent exactly as written — no AI text —
            and a recipient missing a value for a variable is skipped, never sent a broken message.
          </p>
          <Button size={ButtonSize.SMALL} variant={ButtonVariant.GHOST} onClick={handleRefresh}>
            Refresh
          </Button>
        </div>

        {loading && campaigns.length === 0 ? (
          <Spinner />
        ) : campaigns.length === 0 ? (
          <p className="body-sm text-grey-500">No campaigns yet.</p>
        ) : (
          <table className="w-full border-collapse bg-white text-sm">
            <thead>
              <tr className="border-b border-grey-200 text-left text-xs tracking-wide text-grey-600 uppercase">
                <th className="px-3 py-2">Sent</th>
                <th className="px-3 py-2">Starter</th>
                <th className="px-3 py-2">Text</th>
                <th className="px-3 py-2 text-right">Recipients</th>
                <th className="px-3 py-2 text-right">Sent</th>
                <th className="px-3 py-2 text-right">Simulated</th>
                <th className="px-3 py-2 text-right">Skipped</th>
                <th className="px-3 py-2 text-right">Failed</th>
                <th className="px-3 py-2 text-right">Queued</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr
                  key={c.id}
                  className={filterClasses("cursor-pointer border-b border-grey-100 hover:bg-grey-50", selected?.id === c.id && "bg-orange-50")}
                  onClick={() => void handleSelect(c)}
                >
                  <td className="px-3 py-2 whitespace-nowrap text-grey-700">{formatWhen(c.createdAt, "LLL d, h:mm a")}</td>
                  <td className="px-3 py-2">{c.starterLabel}</td>
                  <td className="px-3 py-2 text-grey-600">{c.usedDefault ? "Default" : `Customized (${c.messages.length})`}</td>
                  <td className="px-3 py-2 text-right">{c.recipientCount}</td>
                  <td className="px-3 py-2 text-right">{c.sentCount}</td>
                  <td className="px-3 py-2 text-right">{c.simulatedCount}</td>
                  <td className="px-3 py-2 text-right">{c.skippedCount}</td>
                  <td className="px-3 py-2 text-right">{c.failedCount}</td>
                  <td className="px-3 py-2 text-right">{c.queuedCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {hasMore && (
          <div className="mt-2">
            <Button
              size={ButtonSize.SMALL}
              variant={ButtonVariant.GHOST}
              disabled={loading}
              onClick={() => void load(campaigns[campaigns.length - 1]?.id)}
            >
              Load more
            </Button>
          </div>
        )}

        {selected && (
          <div className="mt-6">
            <h3 className="heading-base-alt mb-2 text-grey-900">
              {selected.starterLabel} — {formatWhen(selected.createdAt, "LLL d, yyyy h:mm a")}
            </h3>
            {selected.messages.length > 0 && (
              <div className="mb-3 flex flex-col gap-1 rounded-md border border-grey-200 bg-white p-3">
                <span className="text-xs font-semibold text-grey-600 uppercase">Authored text</span>
                {selected.messages.map((m, i) => (
                  <p key={i} className="body-sm text-grey-800">
                    {m}
                  </p>
                ))}
              </div>
            )}
            {recipients === null ? (
              <Spinner />
            ) : (
              <table className="w-full border-collapse bg-white text-sm">
                <thead>
                  <tr className="border-b border-grey-200 text-left text-xs tracking-wide text-grey-600 uppercase">
                    <th className="px-3 py-2">User</th>
                    <th className="px-3 py-2">Phone</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2">Reason / error</th>
                    <th className="px-3 py-2">Delivered text</th>
                    <th className="px-3 py-2">Conversation</th>
                  </tr>
                </thead>
                <tbody>
                  {/* The User id opens the customer flyout in place; the Conversation link opens a new tab, so the
                    campaign report stays open either way (ENG-3812). */}
                  {recipients.map((r) => (
                    <tr key={r.id} className="border-b border-grey-100 align-top">
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="cursor-pointer text-orange-600 hover:underline"
                          onClick={() => setSearchParam("userId", String(r.userId))}
                        >
                          {r.userId}
                        </button>
                      </td>
                      <td className="px-3 py-2 font-mono text-grey-700">{r.phoneNumber ?? "—"}</td>
                      <td className="px-3 py-2">
                        <span className={filterClasses("rounded-full px-2 py-0.5 text-xs", STATUS_CLASS[r.status] ?? STATUS_CLASS.queued)}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-grey-700">{r.skipReason ?? r.error ?? ""}</td>
                      <td className="px-3 py-2 text-grey-800">
                        {(r.resolvedMessages ?? []).map((m, i) => (
                          <p key={i}>{m}</p>
                        ))}
                      </td>
                      <td className="px-3 py-2">
                        {r.conversationId ? (
                          <Link
                            className="text-orange-600 hover:underline"
                            to={`/contact-center-v3/conversations/${r.userId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            #{r.conversationId}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                  {recipients.length === 0 && (
                    <tr>
                      <td className="px-3 py-3 text-grey-500" colSpan={7}>
                        No recipients recorded.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      <Panel isOpen={!!selectedUser} onToggle={() => setSearchParam("userId", undefined)}>
        {selectedUser && (
          <UserDetails
            tabs={[
              { id: UserDetailsTabID.Details, label: "Customer Details", badge: false },
              { id: UserDetailsTabID.Notes, label: "Notes", badge: false }
            ]}
            user={selectedUser}
            userVars={userVars}
          />
        )}
      </Panel>
    </>
  );
}
